import { z } from "zod";
import type { Dataset, Field, Project, Values } from "./schema";

const id = z
  .string()
  .min(1)
  .max(200)
  .refine((value) => !!value.trim() && !/[\u0000-\u001f\u007f]/.test(value));
export const statementSelectionSchema = z
  .object({
    text: z.string().max(32768),
    statement: z
      .object({
        version: z.literal(1),
        datasetId: id,
        datasetVersion: z.number().int().positive(),
        statementId: id,
      })
      .strict(),
  })
  .strict();
export type StatementSelection = z.infer<typeof statementSelectionSchema>;
export interface StatementOption {
  id: string;
  label: string;
  text: string;
}
export const statementFieldTypes = ["text", "textarea", "richtext"] as const;

/** Statements reuse pinned table datasets; no executable expressions or remote reads occur. */
export function statementOptions(
  project: Project,
  field: Field,
): StatementOption[] {
  if (
    field.source.kind !== "statement" ||
    !statementFieldTypes.includes(
      field.type as (typeof statementFieldTypes)[number],
    )
  )
    throw new Error("Statements require a text, textarea or richtext field");
  const matches = project.datasets.filter(
    (dataset) =>
      dataset.id === field.source.datasetId &&
      dataset.version === field.source.datasetVersion,
  );
  if (matches.length !== 1 || matches[0].kind !== "table")
    throw new Error("Statement table version is missing or ambiguous");
  const dataset = matches[0],
    content = field.source.resultColumn || "content",
    label =
      field.source.labelColumn || dataset.labelColumn || dataset.primaryKey!;
  if (
    dataset.columns.find((column) => column.key === content)?.type !==
      "string" ||
    !dataset.columns.some((column) => column.key === label)
  )
    throw new Error(
      "Statements require a text content column and a valid label column",
    );
  const options = dataset.rows.map((row) => {
    const key = id.parse(String(row[dataset.primaryKey!] ?? "")),
      text = row[content] ?? "";
    if (typeof text !== "string" || text.length > 32768)
      throw new Error(
        "Statement text must be a string of at most 32768 characters",
      );
    return { id: key, label: String(row[label] ?? "").trim() || key, text };
  });
  if (new Set(options.map((option) => option.id)).size !== options.length)
    throw new Error("Statement IDs must be unique");
  return options;
}
export function selectStatement(
  project: Project,
  field: Field,
  statementId: string,
): StatementSelection {
  const option = statementOptions(project, field).find(
    (item) => item.id === statementId,
  );
  if (!option)
    throw new Error("Statement is unavailable in the configured group version");
  return {
    text: option.text,
    statement: {
      version: 1,
      datasetId: field.source.datasetId!,
      datasetVersion: field.source.datasetVersion!,
      statementId: option.id,
    },
  };
}
export function validateStatementSelection(
  project: Project,
  field: Field,
  value: unknown,
): StatementSelection {
  const parsed = statementSelectionSchema.parse(value),
    source = field.source;
  if (
    parsed.statement.datasetId !== source.datasetId ||
    parsed.statement.datasetVersion !== source.datasetVersion
  )
    throw new Error("Statement selection belongs to another group version");
  const expected = selectStatement(
    project,
    field,
    parsed.statement.statementId,
  );
  if (expected.text !== parsed.text)
    throw new Error(
      "Selected statement content differs from its pinned version",
    );
  return parsed;
}
/** Retain custom text; discard a claimed selection when the next part uses another group/version. */
export function rebindPartValues(
  project: Project,
  values: Values,
  partId?: string,
): Values {
  const next = structuredClone(values),
    part = project.parts.find((item) => item.id === partId);
  for (const base of project.fields) {
    const source = part?.overrides[base.id]?.source || base.source;
    if (source.kind === "dam") {
      delete next[base.id];
      continue;
    }
    const value = next[base.id];
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      Object.hasOwn(value, "statement")
    ) {
      const selected = statementSelectionSchema.safeParse(value);
      if (
        !selected.success ||
        source.kind !== "statement" ||
        selected.data.statement.datasetId !== source.datasetId ||
        selected.data.statement.datasetVersion !== source.datasetVersion
      )
        delete next[base.id];
    }
  }
  return next;
}
/** A plain table also remains editable/uploadable through the reusable dataset tools. */
export function newStatementDataset(
  datasetId: string,
  name = "New statement group",
): Dataset {
  return {
    id: datasetId,
    name,
    version: 1,
    kind: "table",
    choices: [],
    columns: [
      { key: "id", label: "Statement ID", type: "string" },
      { key: "name", label: "Name", type: "string" },
      { key: "content", label: "Content", type: "string", multiline: true },
    ],
    primaryKey: "id",
    valueColumn: "id",
    labelColumn: "name",
    rows: [
      {
        id: "welcome",
        name: "Welcome",
        content:
          "Thank you for choosing our team.\nWe look forward to working with you.",
      },
    ],
  };
}
